import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

// Maps backend document_type -> the public route where the user can review it.
const REVIEW_PATH = {
  customer_tos: "/terms/planner",
  vendor_agreement: "/terms/vendor",
  privacy_policy: "/privacy",
  payment_policy: "/payment-policy",
};

/**
 * Blocking, non-dismissable modal that forces the signed-in user to re-accept
 * any legal documents whose version has changed since their last acceptance.
 *
 * Mounts once at the app root and polls the backend whenever the current user
 * changes (login/logout/refresh). While `required` is non-empty the overlay
 * covers the app and cannot be closed without accepting every doc.
 */
export default function LegalReacceptanceModal() {
  const { user, loading, logout } = useAuth();
  const [required, setRequired] = useState([]);
  const [checked, setChecked] = useState({});   // { document_type: bool }
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);    // finished first fetch for this session

  const fetchRequired = useCallback(async () => {
    if (!user) {
      setRequired([]);
      setChecked({});
      setReady(false);
      return;
    }
    try {
      const { data } = await api.get("/legal-acceptances/reacceptance-required");
      const list = Array.isArray(data?.required) ? data.required : [];
      setRequired(list);
      // Reset checked state to only include currently pending docs.
      setChecked((prev) => {
        const next = {};
        list.forEach((d) => { next[d.document_type] = !!prev[d.document_type]; });
        return next;
      });
    } catch {
      // If we cannot determine required docs, do not block the app.
      setRequired([]);
    } finally {
      setReady(true);
    }
  }, [user]);

  useEffect(() => {
    if (loading) return;
    fetchRequired();
  }, [loading, fetchRequired]);

  if (loading || !user || !ready || required.length === 0) return null;

  const allChecked = required.every((d) => checked[d.document_type]);

  const handleAccept = async () => {
    if (!allChecked || submitting) return;
    setSubmitting(true);
    try {
      // Sequentially POST an acceptance for each pending document.
      for (const d of required) {
        // eslint-disable-next-line no-await-in-loop
        await api.post("/legal-acceptances", {
          document_type: d.document_type,
          document_version: d.version,
          source: "reacceptance",
        });
      }
      toast.success("Thank you — your acceptances have been recorded.");
      await fetchRequired();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not record acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    // Users who refuse must sign out; we cannot let them proceed without accepting.
    await logout();
  };

  return (
    <div
      data-testid="legal-reacceptance-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-reacceptance-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-[color:var(--border)] flex flex-col max-h-[92vh]">
        <div className="p-6 sm:p-8 border-b border-[color:var(--border)]">
          <div className="eyebrow mb-2">Updated Legal Documents</div>
          <h2 id="legal-reacceptance-title" className="text-2xl sm:text-3xl font-semibold">
            Please review and accept our updated terms
          </h2>
          <p className="mt-3 text-sm text-[color:var(--text-muted)] leading-relaxed">
            We&rsquo;ve updated the legal documents that apply to your account. To continue using NContro,
            please review each document below and confirm your acceptance. You must accept every listed
            document before you can proceed.
          </p>
        </div>

        <div className="p-6 sm:p-8 overflow-y-auto space-y-4">
          {required.map((d) => {
            const path = REVIEW_PATH[d.document_type];
            const id = `legal-reaccept-item-${d.document_type}`;
            return (
              <div
                key={d.document_type}
                data-testid={id}
                className="rounded-xl border border-[color:var(--border)] p-4 sm:p-5 flex items-start gap-3"
              >
                <Checkbox
                  id={`${id}-checkbox`}
                  data-testid={`legal-reaccept-checkbox-${d.document_type}`}
                  checked={!!checked[d.document_type]}
                  onCheckedChange={(v) =>
                    setChecked((prev) => ({ ...prev, [d.document_type]: v === true }))
                  }
                  className="mt-1"
                />
                <div className="flex-1">
                  <label
                    htmlFor={`${id}-checkbox`}
                    className="text-sm sm:text-base font-medium text-[color:var(--text)] cursor-pointer"
                  >
                    I have read and accept the updated {d.label}
                    <span className="ml-2 text-xs text-[color:var(--text-muted)] font-normal">
                      Version {d.version}
                    </span>
                  </label>
                  {path && (
                    <div className="mt-1">
                      <a
                        href={path}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`legal-reaccept-review-${d.document_type}`}
                        className="text-sm link-underline text-[color:var(--accent)]"
                      >
                        Review {d.label} →
                      </a>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-6 sm:p-8 border-t border-[color:var(--border)] flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <button
            type="button"
            onClick={handleDecline}
            data-testid="legal-reaccept-decline-btn"
            className="text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text)] underline underline-offset-2 self-start sm:self-center"
          >
            Decline and sign out
          </button>
          <Button
            type="button"
            onClick={handleAccept}
            disabled={!allChecked || submitting}
            data-testid="legal-reaccept-accept-btn"
            className="sm:min-w-[220px]"
          >
            {submitting ? "Recording…" : "Accept & Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
